import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { CinematicBurst } from './CinematicBurst';
import { RevealPhotos } from './RevealPhotos';
import { useRevealViewport, PreparingIndicator } from '../useRevealViewport';
import type { FullCanvasSceneProps } from './types';
import { STYLE_ALIGNMENT } from './types';
import { AdaptiveMessageBody } from '../cinematic/adaptive/AdaptiveMessageBody';
import { getReadingRevealCompleteMs } from './readingDwell';

import { DepthRevealLayer } from '../cinematic/DepthRevealLayer';
const EASE = [0.22, 0.61, 0.36, 1] as const;

const FLORAL_COLORS = [
  '#f43f5e', '#ec4899', '#e11d48', '#fb7185', '#f472b6',
  '#c026d3', '#a855f7', '#f97316', '#fbbf24', '#84cc16',
  '#10b981', '#14b8a6', '#f9a8d4', '#fda4af', '#d946ef',
];

export function FullCanvasFloral({ recipientName, relationship, message, photoUrls, publishedDate, onComplete, config, startAtMessage = false }: FullCanvasSceneProps) {
  const [phase, setPhase] = useState<'atmosphere' | 'burst' | 'bloom' | 'text' | 'done'>(startAtMessage ? 'text' : 'atmosphere');
  const [burstFired, setBurstFired] = useState(false);
  const lines = message.split('\n').filter(l => l.trim());
  const lineDelay = 0.6;
  const { isMobile, burstOriginY, timingMultiplier: tm, particleScale } = useRevealViewport();

  const t = (ms: number) => ms * tm;

  useEffect(() => {
    if (startAtMessage) {
      const tDone = setTimeout(() => { setPhase('done'); onComplete(); }, getReadingRevealCompleteMs({
        message,
        mode: config?.style.readingRhythm ?? 'sentence',
        pacing: config?.pacing ?? 1,
        startDelay: 1.2,
      }));
      return () => clearTimeout(tDone);
    }
    const t1 = setTimeout(() => { setPhase('burst'); setBurstFired(true); }, t(4000));
    const t2 = setTimeout(() => setPhase('bloom'), t(7000));
    const t3 = setTimeout(() => setPhase('text'), t(11000));
    const totalDuration = t(11000) / 1000 + 2.0 + lines.length * lineDelay + 4.0;
    const t4 = setTimeout(() => { setPhase('done'); onComplete(); }, totalDuration * 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [config?.pacing, config?.style.readingRhythm, lines.length, message, onComplete, startAtMessage, tm]);

  const isPostBurst = phase !== 'atmosphere';
  const isTextVisible = phase === 'text' || phase === 'done';

  const petalCount = Math.round(20 * particleScale);
  const petalRain = useMemo(() =>
    Array.from({ length: petalCount }, (_, i) => ({
      x: Math.random() * 100,
      size: 6 + Math.random() * 12,
      duration: 7 + Math.random() * 6,
      delay: t(4500) / 1000 + Math.random() * 4,
      drift: (Math.random() - 0.5) * 60,
      color: FLORAL_COLORS[i % FLORAL_COLORS.length],
      rotation: Math.random() * 360,
    })),
  [petalCount, tm]);

  return (
    <div className={`min-h-[100dvh] sm:min-h-screen relative ${isTextVisible ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'}`}>
      <motion.div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #1a0a10 0%, #2d1020 40%, #1a0a10 100%)' }}
        animate={{ opacity: isPostBurst ? 0 : 1 }}
        transition={{ duration: 2.5, ease: EASE as any }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-pink-50/80 to-amber-50/60" />
      <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Atmosphere pulse — centered for mobile */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% ${burstOriginY}%, rgba(244,63,94,0.15) 0%, transparent 40%)` }}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={phase === 'atmosphere' ? { opacity: [0, 0.8, 0.4, 0.8], scale: [0.5, 1.2, 0.9, 1.2] } : { opacity: 0 }}
        transition={phase === 'atmosphere' ? { duration: 3, repeat: Infinity, ease: 'easeInOut' } : { duration: 1 }}
      />

      {/* Mobile preparing indicator */}
      {isMobile && <PreparingIndicator visible={phase === 'atmosphere'} color="rgba(244,63,94,0.7)" />}

      {phase === 'atmosphere' && (
        <>
          {[0, 1, 2, 3, 4].map(i => (
            <motion.div
              key={`pre-${i}`}
              className="absolute rounded-full pointer-events-none"
              style={{
                width: isMobile ? 5 + i * 3 : 3 + i * 2, height: isMobile ? 5 + i * 3 : 3 + i * 2,
                left: `${30 + i * 10}%`, top: `${(burstOriginY - 10) + (i % 3) * 10}%`,
                background: FLORAL_COLORS[i],
                boxShadow: `0 0 20px 8px ${FLORAL_COLORS[i]}44`,
              }}
              animate={{ opacity: [0, 0.6, 0], scale: [0.5, 1.5, 0.5] }}
              transition={{ duration: 2, delay: i * 0.4, repeat: Infinity, ease: 'easeInOut' }}
            />
          ))}
        </>
      )}

      <motion.div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(251,191,36,0.08) 0%, transparent 55%)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isPostBurst ? 1 : 0 }}
        transition={{ duration: 2, delay: 0.5 }}
      />

      <CinematicBurst active={burstFired} count={Math.round(70 * particleScale)} colors={FLORAL_COLORS} shape="petal" originX={50} originY={burstOriginY} spread={isMobile ? 45 : 60} ambientCount={Math.round(22 * particleScale)} glowIntensity={3} />

      {isPostBurst && petalRain.map((p, i) => (
        <motion.div
          key={`rain-${i}`}
          className="absolute pointer-events-none"
          style={{ left: `${p.x}%`, top: '-5%', zIndex: 40 }}
          animate={{
            y: [0, typeof window !== 'undefined' ? window.innerHeight * 1.3 : 1200],
            x: [0, p.drift * 0.5, p.drift],
            rotate: [0, p.rotation, p.rotation * 2],
            opacity: [0, 0.7, 0.5, 0],
          }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'linear' }}
        >
          <svg width={p.size} height={p.size * 1.4} viewBox="0 0 20 28">
            <ellipse cx="10" cy="14" rx="8" ry="13" fill={p.color} opacity="0.7" />
            <ellipse cx="10" cy="12" rx="5" ry="9" fill="white" opacity="0.2" />
          </svg>
        </motion.div>
      ))}

      {/* Botanical corners — scaled for mobile */}
      {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map((pos, i) => (
        <motion.div
          key={pos}
          className={`absolute pointer-events-none ${
            pos === 'top-left' ? 'top-0 left-0' :
            pos === 'top-right' ? 'top-0 right-0' :
            pos === 'bottom-left' ? 'bottom-0 left-0' :
            'bottom-0 right-0'
          }`}
          initial={{ opacity: 0, scale: 0.3 }}
          animate={isPostBurst ? { opacity: 0.8, scale: 1 } : {}}
          transition={{ delay: t(3500) / 1000 + i * 0.3, duration: 1.8, ease: EASE as any }}
        >
          <svg viewBox="0 0 200 200" className={`${isMobile ? 'w-20 h-20' : 'w-36 h-36'} md:w-52 md:h-52 text-rose-300/60`}
            style={{ transform: `${pos.includes('right') ? 'scaleX(-1)' : ''} ${pos.includes('bottom') ? 'scaleY(-1)' : ''}` }}
          >
            <ellipse cx="40" cy="60" rx="25" ry="40" fill="currentColor" opacity="0.3" transform="rotate(-30 40 60)" />
            <ellipse cx="80" cy="40" rx="20" ry="35" fill="currentColor" opacity="0.25" transform="rotate(-60 80 40)" />
            <ellipse cx="120" cy="80" rx="15" ry="25" fill="currentColor" opacity="0.35" transform="rotate(-45 120 80)" />
            <ellipse cx="140" cy="60" rx="15" ry="25" fill="currentColor" opacity="0.3" transform="rotate(-10 140 60)" />
            <ellipse cx="150" cy="90" rx="15" ry="25" fill="currentColor" opacity="0.25" transform="rotate(30 150 90)" />
            <circle cx="135" cy="80" r="8" fill="rgba(251,191,36,0.4)" />
            <circle cx="55" cy="45" r="6" fill="rgba(244,63,94,0.3)" />
            <path d="M40 60 Q50 80 60 100 Q70 120 60 140" stroke="currentColor" strokeWidth="1" opacity="0.2" fill="none" />
          </svg>
        </motion.div>
      ))}

      <motion.div
        className="relative z-30 py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: isTextVisible ? 1 : 0 }}
        transition={{ duration: startAtMessage ? 1.8 : 1.2 }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isTextVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.0, ease: EASE as any }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-rose-500/60 mb-3">A message for you</p>
            <h1 className="font-serif text-3xl md:text-5xl text-rose-900/90 mb-4 tracking-wide">
              Dear {recipientName}
            </h1>
            <motion.div
              className="flex items-center justify-center gap-4 mb-8"
              initial={{ scaleX: 0 }}
              animate={isTextVisible ? { scaleX: 1 } : {}}
              transition={{ delay: 0.5, duration: 1.0, ease: EASE as any }}
            >
              <div className="w-16 h-px bg-rose-300/40" />
              <Heart className="h-4 w-4 text-rose-400/60" fill="currentColor" />
              <div className="w-16 h-px bg-rose-300/40" />
            </motion.div>
          </motion.div>

          <RevealPhotos photoUrls={photoUrls} visible={isTextVisible} delay={0.8} borderColor="rgba(255,255,255,0.7)" shadowColor="rgba(244,63,94,0.15)" />

          <div className="mb-12">
            <AdaptiveMessageBody
              message={message}
              mode={config?.style.readingRhythm ?? 'sentence'}
              pacing={config?.pacing ?? 1}
              visible={isTextVisible}
              startDelay={1.2}
              alignment={config ? STYLE_ALIGNMENT[config.styleId] : 'centered'}
              textColorClass="text-rose-900/85"
              emphasisGlow="rgba(244,114,182,0.45)"
              typography={config?.typography ?? "serif-classic"}
            />
          </div>

          <motion.div initial={{ opacity: 0 }} animate={isTextVisible ? { opacity: 1 } : {}}
            transition={{ delay: 1.8 + lines.length * lineDelay, duration: 1.0 }}
          >
            <p className="text-lg font-serif italic text-rose-700/70 mb-2">With all my love,</p>
            <p className="text-xl font-serif text-rose-800/80">— From your {getSenderPerspectiveLabel(relationship)}</p>
            <div className="mt-10 pt-6 border-t border-rose-200/30">
              <p className="text-xs text-rose-500/40 tracking-wider">Say It NOW • While They're Still Here</p>
              {publishedDate && <p className="text-xs text-rose-400/30 mt-1">Shared on {publishedDate}</p>}
            </div>
          </motion.div>
        </div>
      </motion.div>
      <DepthRevealLayer active={isTextVisible} tint="rgba(244,114,182,0.55)" glow="rgba(253,164,175,0.5)" />
    </div>
  );
}
